import React, { useState, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import { Search } from 'semantic-ui-react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

const initialState = {
	loading: false,
	results: [],
	value: '',
	itemSelected: null,
	open: false,
}

function exampleReducer(state, action) {
	switch (action.type) {
		case 'CLEAN_QUERY':
			return initialState
		case 'START_SEARCH':
			return { ...state, loading: true, value: action.query, open: true }
		case 'FINISH_SEARCH':
			return { ...state, loading: false, results: action.results || [] }
		case 'UPDATE_SELECTION':
			return {
				...state,
				value: action.selection.title,
				itemSelected: action.selection,
				open: false,
			}
		case 'TOOGLE_SEARCH':
			return { ...state, open: action.open }
		case 'CLEAN_VALUE':
			return { ...state, value: '', open: false }
		case 'SET_DEFAULT_VALUE':
			return { ...state, value: action.value }

		default:
			throw new Error()
	}
}

const CustomSearch = (props) => {
	const { t } = useTranslation()
	const {
		options,
		onSelectOption,
		onNotFoundOption,
		onChange,
		clearSelection,
		tabIndex,
		initialValueIndex,
		minCharacters,
		willFocus = false,
		clearOnEmpty = false,
		onBlur,
		searchOnExit = true,
		placeholder,
		inputStyle,
		size = 'large',
		fullWidth = false,
		maxResults = 50, // Límite de resultados para mejor performance
		enableFuzzySearch = false, // Búsqueda con tolerancia a errores
	} = props

	const [enterWasPressedWhileSearching, setEnterWasPressedWhileSearching] =
		useState(false)

	const [state, dispatch] = useReducer(exampleReducer, initialState)
	const { loading, results, value, open } = state

	const timeoutRef = React.useRef()
	const mainRef = React.useRef(null)
	const searchCacheRef = React.useRef({}) // Caché de búsquedas
	const wrapperRef = React.useRef(null)
	const [portalPos, setPortalPos] = useState({ top: 0, left: 0, width: 0 })

	const updatePortalPos = () => {
		const el = wrapperRef.current
		if (!el) return
		const r = el.getBoundingClientRect()
		setPortalPos({
			top: r.bottom,
			left: r.left,
			width: r.width,
		})
	}

	useEffect(() => {
		if (!open) return
		updatePortalPos()
		window.addEventListener('scroll', updatePortalPos, true)
		window.addEventListener('resize', updatePortalPos)
		return () => {
			window.removeEventListener('scroll', updatePortalPos, true)
			window.removeEventListener('resize', updatePortalPos)
		}
	}, [open, value])
	// Calcular similitud entre dos strings (Levenshtein simplificado)
	const calculateSimilarity = (str1, str2) => {
		const longer = str1.length > str2.length ? str1 : str2
		const shorter = str1.length > str2.length ? str2 : str1

		if (longer.length === 0) return 1.0

		const editDistance = (s1, s2) => {
			s1 = s1.toLowerCase()
			s2 = s2.toLowerCase()

			const costs = []
			for (let i = 0; i <= s1.length; i++) {
				let lastValue = i
				for (let j = 0; j <= s2.length; j++) {
					if (i === 0) {
						costs[j] = j
					} else if (j > 0) {
						let newValue = costs[j - 1]
						if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
							newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
						}
						costs[j - 1] = lastValue
						lastValue = newValue
					}
				}
				if (i > 0) costs[s2.length] = lastValue
			}
			return costs[s2.length]
		}

		return (longer.length - editDistance(longer, shorter)) / longer.length
	}

	const handleSearchChange = React.useCallback(
		(e, data) => {
			clearTimeout(timeoutRef.current)
			dispatch({ type: 'START_SEARCH', query: data.value })

			timeoutRef.current = setTimeout(() => {
				if (data.value.length === 0 && clearOnEmpty) {
					dispatch({ type: 'CLEAN_QUERY' })
					return
				}

				// Verificar caché primero
				const cacheKey = data.value.toLowerCase()
				if (searchCacheRef.current[cacheKey]) {
					dispatch({
						type: 'FINISH_SEARCH',
						results: searchCacheRef.current[cacheKey],
					})
					return
				}

				// Normalizar texto: remover acentos y convertir a minúsculas
				const normalize = (text) => {
					if (!text) return ''
					return text
						.toString()
						.toLowerCase()
						.normalize('NFD')
						.replace(/[\u0300-\u036f]/g, '')
						.trim()
				}

				const normalizedQuery = normalize(data.value)

				// Dividir la consulta en palabras para búsqueda por palabras clave
				const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0)

				// Función mejorada de coincidencia con scoring
				const scoreResult = (result) => {
					// Normalizar campos de búsqueda
					const normalizedTitle = normalize(result.title)
					const normalizedDescription = normalize(result.description)
					const normalizedKeywords = normalize(result.keywords || '')
					const normalizedCode = normalize(result.code || '')
					const normalizedTags = normalize(result.tags || '')

					const searchText = `${normalizedTitle} ${normalizedDescription} ${normalizedKeywords} ${normalizedCode} ${normalizedTags}`

					let score = 0
					let matched = false

					// Coincidencia exacta en título = máxima prioridad
					if (normalizedTitle === normalizedQuery) {
						score = 1000
						matched = true
					}
					// Coincidencia exacta en código
					else if (normalizedCode === normalizedQuery) {
						score = 900
						matched = true
					}
					// Título comienza con la búsqueda
					else if (normalizedTitle.startsWith(normalizedQuery)) {
						score = 800
						matched = true
					}
					// Código comienza con la búsqueda
					else if (normalizedCode.startsWith(normalizedQuery)) {
						score = 700
						matched = true
					}
					// Título contiene la búsqueda completa
					else if (normalizedTitle.includes(normalizedQuery)) {
						score = 600
						matched = true
					}
					// Descripción contiene la búsqueda completa
					else if (normalizedDescription.includes(normalizedQuery)) {
						score = 500
						matched = true
					}
					// Keywords o tags contienen la búsqueda
					else if (normalizedKeywords.includes(normalizedQuery) || normalizedTags.includes(normalizedQuery)) {
						score = 400
						matched = true
					}
					// Búsqueda por palabras clave individuales
					else if (queryWords.every(word => searchText.includes(word))) {
						// Calcular score basado en cuántas palabras coinciden
						const wordMatches = queryWords.filter(word => {
							return normalizedTitle.includes(word) || normalizedCode.includes(word)
						}).length
						score = 200 + (wordMatches * 50)
						matched = true
					}
					// Búsqueda fuzzy (si está habilitada y no hay coincidencias exactas)
					else if (enableFuzzySearch) {
						const titleSimilarity = calculateSimilarity(normalizedTitle, normalizedQuery)
						const codeSimilarity = normalizedCode ? calculateSimilarity(normalizedCode, normalizedQuery) : 0

						// Solo considerar si la similitud es mayor al 70%
						if (titleSimilarity > 0.7 || codeSimilarity > 0.7) {
							score = Math.max(titleSimilarity, codeSimilarity) * 100
							matched = true
						}
					}

					return { result, score, matched }
				}

				// Filtrar y puntuar resultados
				const scoredResults = options
					.map(scoreResult)
					.filter(item => item.matched)
					.sort((a, b) => {
						// Ordenar por score descendente
						if (b.score !== a.score) return b.score - a.score
						// Si tienen el mismo score, ordenar alfabéticamente por título
						return a.result.title.localeCompare(b.result.title)
					})
					.slice(0, maxResults) // Limitar número de resultados
					.map(item => item.result)

				// Guardar en caché
				searchCacheRef.current[cacheKey] = scoredResults

				// Limpiar caché si crece mucho (mantener últimas 20 búsquedas)
				const cacheKeys = Object.keys(searchCacheRef.current)
				if (cacheKeys.length > 20) {
					const oldestKey = cacheKeys[0]
					delete searchCacheRef.current[oldestKey]
				}

				dispatch({
					type: 'FINISH_SEARCH',
					results: scoredResults,
				})
			}, 300)

			if (onChange) {
				onChange(data.value)
			}
		},
		[options, clearOnEmpty, onChange, maxResults, enableFuzzySearch]
	)

	useEffect(() => {
		if (willFocus) {
			mainRef.current.focus()
			dispatch({
				type: 'TOOGLE_SEARCH',
				open: true,
			})
		}
	}, [willFocus])

	useEffect(() => {
		dispatch({
			type: 'FINISH_SEARCH',
			results: options,
		})
	}, [options])

	useEffect(() => {
		if (!loading) {
			if (enterWasPressedWhileSearching) {
				handleKeyDownOnFinishSearch()
				setEnterWasPressedWhileSearching(false)
			}
		}
	}, [loading, enterWasPressedWhileSearching])

	useEffect(() => {
		dispatch({
			type: 'SET_DEFAULT_VALUE',
			value: props.value,
		})
	}, [props.value])

	const clear = () => {
		dispatch({
			type: 'CLEAN_VALUE',
		})
	}

	if (clearSelection) {
		clearSelection(clear)
	}

	useEffect(() => {
		if (initialValueIndex >= 0) {
			const option = options[initialValueIndex]
			if (option) {
				dispatch({
					type: 'UPDATE_SELECTION',
					selection: option,
					item: option,
				})
			}
		}
	}, [initialValueIndex])

	const handleKeyDownOnFinishSearch = () => {
		if ((results || []).length === 1) {
			const result = (results || [])[0]
			onSelectOption(result, clear)
			dispatch({
				type: 'CLEAN_QUERY',
			})

			document.activeElement.blur()
			setTimeout(() => {
				mainRef.current.focus()
			}, 100)
		} else if ((results || []).length === 0) {
			onNotFoundOption(value, clear)
		}
	}

	const onKeyDown = (e) => {
		const isEnter = e.keyCode === 13 || (e.keyCode === 9 && searchOnExit)
		const isEscape = e.keyCode === 27

		// ESC para limpiar búsqueda
		if (isEscape) {
			dispatch({ type: 'CLEAN_QUERY' })
			if (onChange) onChange('')
			e.preventDefault()
			e.stopPropagation()
			return
		}

		if (isEnter) {
			if (loading) {
				setEnterWasPressedWhileSearching(true)
			} else {
				handleKeyDownOnFinishSearch(results)
			}

			e.preventDefault()
			e.stopPropagation()
		}
	}
	const defaultInputStyle = {
		height: 40,
		fontSize: 16,
		//padding: '10px 14px',
		...inputStyle, // permite sobrescribir
	}

	const dropdownContent = (
		<div className={`ui fluid search ${open ? "active" : ""} ${props.resultsClassName || ""}`}>
			<div className={`results transition ${open ? "visible" : "hidden"}`} style={{ display: open ? "block" : "none" }}>
				{(results || []).map((r) => (
					<a
						key={r.key || r.id || `${r.title}-${r.description}`}
						className="result"
						onMouseDown={(e) => e.preventDefault()} // evita que blur cierre antes del click
						onClick={() => {
							onSelectOption(r)
							dispatch({ type: 'UPDATE_SELECTION', selection: r })
						}}
						style={{ cursor: "pointer" }}
					>
						<div className="content">
							<div className="title">{r.title}</div>
							{r.description ? <div className="description">{r.description}</div> : null}
						</div>
					</a>
				))}
			</div>
		</div>
	);

	return (
		<div ref={wrapperRef} className={props.className} style={{ position: 'relative', ...(props.fullWidth && { width: '100%' }) }}>
			<Search
				open={false}
				loading={loading}
				tabIndex={tabIndex}
				size={size}
				input={{
					ref: mainRef,
					placeholder: props.placeholder || t("Buscar proceso"),
					style: { ...defaultInputStyle, ...(props.fullWidth && { width: '100%' }) },
				}}
				onSearchChange={handleSearchChange}
				value={value}
				minCharacters={minCharacters}
				onFocus={() => {
					mainRef.current.focus()
					dispatch({ type: 'TOOGLE_SEARCH', open: true })
					setTimeout(updatePortalPos, 0)
				}}
				onBlur={(e, data) => {
					// delay para permitir click en resultados
					setTimeout(() => {
						if (onBlur) onBlur(data.value)
						dispatch({ type: 'TOOGLE_SEARCH', open: false })
					}, 140)
				}}
				onKeyDown={onKeyDown}
			/>

			{open && results?.length > 0 && (
				props.portaled === false ? (
					<div
						style={{
							position: 'absolute',
							top: '100%',
							left: 0,
							width: '100%',
							zIndex: 999999,
						}}
					>
						{dropdownContent}
					</div>
				) : (
					ReactDOM.createPortal(
						<div
							style={{
								position: 'fixed',
								top: portalPos.top,
								left: portalPos.left,
								width: portalPos.width,
								zIndex: 999999,
							}}
						>
							{dropdownContent}
						</div>,
						document.body
					)
				)
			)}
		</div>
	)

}

CustomSearch.propTypes = {
	onSelectOption: PropTypes.func,
	onChange: PropTypes.func,
	options: PropTypes.array,
	minCharacters: PropTypes.number,
	value: PropTypes.string,
	maxResults: PropTypes.number,
	enableFuzzySearch: PropTypes.bool,
	placeholder: PropTypes.string,
	inputStyle: PropTypes.object,
	onNotFoundOption: PropTypes.func,
	onBlur: PropTypes.func,
	clearSelection: PropTypes.func,
	tabIndex: PropTypes.number,
	initialValueIndex: PropTypes.number,
	willFocus: PropTypes.bool,
	clearOnEmpty: PropTypes.bool,
	searchOnExit: PropTypes.bool,
	className: PropTypes.string,
	resultsClassName: PropTypes.string,
}

export default CustomSearch
